import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { AlertHistoryEntry, AlertRule, PaginatedResponse } from '@/lib/types'

const RULES_KEY = ['alerts', 'rules'] as const
const HISTORY_KEY = ['alerts', 'history'] as const

export function useAlertRules(): ReturnType<typeof useQuery<AlertRule[]>> {
  return useQuery({
    queryKey: RULES_KEY,
    queryFn: () => apiClient.get<AlertRule[]>(endpoints.alerts.rules),
  })
}

interface UpdateRulePayload {
  id: string
  threshold: number
  severity: 'warning' | 'critical'
  enabled: boolean
  notify_email: boolean
  notify_slack: boolean
  cooldown_minutes: number
}

export function useUpdateAlertRule(): ReturnType<
  typeof useMutation<unknown, unknown, UpdateRulePayload>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateRulePayload) =>
      apiClient.put(endpoints.alerts.updateRule(id), body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RULES_KEY })
    },
  })
}

interface AlertHistoryParams {
  page?: number
  per_page?: number
  severity?: string
}

export function useAlertHistory(
  params?: AlertHistoryParams,
): ReturnType<typeof useQuery<PaginatedResponse<AlertHistoryEntry>>> {
  return useQuery({
    queryKey: [...HISTORY_KEY, params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<AlertHistoryEntry>>(
        endpoints.alerts.history({ page: params?.page, per_page: params?.per_page }),
      ),
  })
}

export function useTestAlert(): ReturnType<typeof useMutation<unknown, unknown, void>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post(endpoints.alerts.test),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: HISTORY_KEY })
    },
  })
}
